import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { acquisitionsApi, type AcquisitionRequest } from "@/lib/api/acquisitions";
import { useToast } from "@/contexts/ToastContext";
import { getApiErrorMessage } from "@/lib/api-error";
import { StatusBadge } from "@/components/ui/status-badge";
import { statusVariantMap } from "@/pages/acquisitions/AcquisitionRequestsPage";
import { Loader2, ChevronRight, BookPlus } from "lucide-react";

export default function MyRecommendationsPage() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState<AcquisitionRequest[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await acquisitionsApi.requests.listMy({ limit: 50 });
      setRequests(res.requests);
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, "Could not load your recommendations."));
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <PageContainer width="wide">
      <PageHeader
        title="My recommendations"
        description="Purchase requests you submitted. Open a request to reply when staff ask for more information."
        showSearch={false}
        titleSize="md"
        actions={
          <Button asChild>
            <Link to="/recommend">
              <BookPlus className="mr-2 h-4 w-4" />
              Recommend a title
            </Link>
          </Button>
        }
      />

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      ) : requests.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white p-10 text-center">
          <p className="text-slate-600">You have not submitted any recommendations yet.</p>
          <Button asChild className="mt-4">
            <Link to="/recommend">Recommend a title</Link>
          </Button>
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white divide-y">
          {requests.map((r) => {
            const cfg = statusVariantMap[r.status] || {
              variant: "neutral" as const,
              label: r.status,
            };
            return (
              <Link
                key={r.id}
                to={`/my-recommendations/${r.id}`}
                className="flex items-center justify-between gap-4 px-4 py-4 hover:bg-slate-50 transition-colors"
              >
                <div className="min-w-0 text-left">
                  <p className="font-medium text-slate-900 truncate">{r.title}</p>
                  {r.author && <p className="text-sm text-slate-500 truncate">{r.author}</p>}
                  <p className="text-xs text-slate-400 mt-1">
                    Submitted {new Date(r.createdAt).toLocaleDateString()}
                  </p>
                  {r.status === "NEEDS_INFO" && (
                    <p className="text-xs font-medium text-amber-700 mt-1">
                      Staff need more information — open to respond
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <StatusBadge variant={cfg.variant}>{cfg.label}</StatusBadge>
                  <ChevronRight className="h-4 w-4 text-slate-400" />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </PageContainer>
  );
}
